/**
 * WorkingGroups Page Component - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * A comprehensive working groups overview page with advanced features:
 * - Working groups visual hierarchy display
 * - Interactive working group selection and navigation
 * - Participation statistics and analytics
 * - Working group updates overview
 * - Members council and Irish Funds executives integration
 * - Learn more modal with detailed information
 * - Multi-section layout with responsive design
 * - Data transformation for working group structure
 * 
 * Features:
 * - WGVisual component with interactive group display
 * - Working group structure and types integration
 * - Participation data visualization
 * - Periodic updates overview with filtering
 * - Info modal for detailed working group information
 * - HTML content parsing for rich text display
 * - Members council and executives data integration
 * - Responsive section layout with dividers
 * 
 * @returns {JSX.Element} WorkingGroups page component with comprehensive working groups overview
 */
import React, { useEffect, useState } from 'react';
import Divider from '../components/Common/Divider';
import WGVisual from '../components/WorkingGroups/WGVisual';
import apiHelper from '../utils/apiHelper';
import InfoModal from '../modals/InfoModal';
import WGParticipation from '../components/WorkingGroups/WGParticipation';
import Parser from 'html-react-parser';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import { transformWorkingGroups } from '../utils/transformWorkingGroups';
import { FILE_NAMES } from '../utils/constants';
import WGUpdatesOverview from '../components/WorkingGroups/WGUpdatesOverview';

import './WorkingGroups.css';

/**
 * WorkingGroups component for displaying working groups overview
 * with visual hierarchy, participation data, and updates
 */
function WorkingGroups() {
  //Data
  const [wgData, setWgData] = useState([]);
  const [infoModalShow, setInfoModalShow] = useState(false);
  const [participationData, setParticipationData] = useState(false);
  const [welcomeBlobContent, setWelcomeBlobContent] = useState('');
  const [diagramBlobContent, setDiagramBlobContent] = useState('');

  const [allUpdates, setAllUpdates] = useState([]);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const pageContentResult = await apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.PAGECONTENT);  
        const welcomeBlobContent = pageContentResult.fileContent.find(item => item.id === 4);
        setWelcomeBlobContent(welcomeBlobContent?.htmlContent ? String(welcomeBlobContent.htmlContent) : 'No content available');
        const diagramBlobContent = pageContentResult.fileContent.find(item => item.id === 5);
        setDiagramBlobContent(diagramBlobContent?.htmlContent ? String(diagramBlobContent.htmlContent) : 'No content available');

        const updatesResult = await apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.WG_PERIODIC_UPDATES);
        setAllUpdates(updatesResult.fileContent || []);
        
      } catch (err) {
        console.error('Error fetching content:', err);
      }
    };

    fetchContent();
  }, []);


  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch both structure and types data
        const [wgStructureResult, wgTypesResult] = await Promise.all([
          apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.WG_STRUCTURE),
          apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.WG_TYPES)
        ]);
        
        const transformedData = transformWorkingGroups(
          wgStructureResult.fileContent, 
          wgTypesResult.fileContent
        );
        setWgData(transformedData);

        const participationDataResult = await apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.WG_PARTICIPATION);
        setParticipationData(participationDataResult.fileContent);

      } catch (err) {
        console.error('Error fetching data:', err);
      }
    };
    fetchData();
  }, []);

  return (
    <div className='totalPage'>
      <section className="wrapper">
        <div id="introduction" className="container mt-6">
          <div className='wg-intro pb-10'>
            {welcomeBlobContent && typeof welcomeBlobContent === 'string' ? 
              Parser(welcomeBlobContent) : 
              <LoadingSpinner />
            }
          </div>
          <div className='align-right'>
            <button 
              className="btn btn-primary btn-compact" 
              onClick={() => setInfoModalShow(true)} 
              aria-label="Learn more"
            >
              <i className="fa-solid fa-circle-info"></i> Learn More
            </button>
          </div>
          {infoModalShow && (
            <InfoModal
              show={infoModalShow}
              onClose={() => setInfoModalShow(false)}
              small={true}
              data={diagramBlobContent && typeof diagramBlobContent === 'string' ? 
                diagramBlobContent: 
                <LoadingSpinner />
              }
              title={'Working Groups Info'}
            />
          )}
        </div>

      </section>
      <Divider height={60} />
      <section className="wrapper bg-gradient-primary">
        <div id="accordions" className="container">
          <WGVisual workingGroupsdata={wgData} />
        </div>
      </section>

      <Divider height={60} />

      <section id="participation" className="wrapper bg-gradient-primary">
        <div className="container">
          <h2 className="text-uppercase main-header">Working Group Participation: Collective Strength</h2>
        </div>
        <div className="container">
          <div className='wg-participation-container'>
            <WGParticipation data={participationData} />
          </div>
        </div>
      </section>

      <Divider height={60} />

      <section className="wrapper bg-gradient-primary pb-16">
        <div className="container">
          <h2 className="text-uppercase main-header">Working Group Updates</h2>
        </div>
        <div className="container">
          <WGUpdatesOverview updates={allUpdates} workingGroups={wgData} />
        </div>
      </section>

    </div>
  );
}

export default WorkingGroups;